// src/pages/utilities/UtilityListPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { useBreadcrumb } from "../../context/BreadcrumbContext";

import {
  getUtilityParentDetails,
  deleteUtility,
} from "../../api/utilityApi";
import { getPropertyById } from "../../api/unitApi";

import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

function UtilityListPage() {
  const { propertyId, unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();

  const [utilities, setUtilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const isUnitContext = Boolean(unitId);
  const userRoles = user?.claims_role || [];
  const canEdit = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("PUT")
  );
  const canCreate = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("POST")
  );
  const canDelete = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("DELETE")
  );
  const showActions = canDelete;

  // Load utilities and set breadcrumbs
  useEffect(() => {
    const fetchUtilities = async () => {
      try {
        const data = await getUtilityParentDetails(propertyId, unitId);
        setUtilities(data.utilities || []);

        // Set breadcrumb path
        if (isUnitContext) {
          const propertyData = await getPropertyById(data.unit.property_id);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: propertyData.name, path: `/properties/${data.unit.property_id}/details` },
            {
              label: `Unit ${data.unit.unit_name || data.unit.unit_code || data.unit.id}`,
              path: `/units/${unitId}/details`,
            },
            { label: "Utilities", path: null },
          ]);
        } else {
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: data.name, path: `/properties/${propertyId}/details` },
            { label: "Utilities", path: null },
          ]);
        }
      } catch (err) {
        console.error("Failed to load utilities:", err);
        setError("Failed to load utilities.");
      } finally {
        setLoading(false);
      }
    };

    fetchUtilities();
    return () => clearCustomPath();
  }, [propertyId, unitId, isUnitContext, setCustomPath, clearCustomPath]);

  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (!incomingToast?.message) return;
    showToast(incomingToast.message, incomingToast.severity || "success");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // Delete utility
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this utility?")) return;

    try {
      await deleteUtility(id);
      setUtilities((prev) => prev.filter((u) => u.id !== id));
      showToast("Utility deleted");
    } catch {
      showToast("Failed to delete utility.", "error");
    }
  };

  // Add / Edit navigation
  const handleAdd = () => {
    navigate(
      isUnitContext
        ? `/units/${unitId}/utilities/create`
        : `/properties/${propertyId}/utilities/create`
    );
  };

  // LOADING STATE
  if (loading)
    return (
      <Layout>
        <div className="page-container">
          <Typography>Loading utilities...</Typography>
        </div>
      </Layout>
    );

  // ERROR STATE
  if (error)
    return (
      <Layout>
        <div className="page-container">
          <Alert severity="error">{error}</Alert>
        </div>
      </Layout>
    );

  return (
    <Layout>
      <Box className="page-container">
        {/* PAGE TITLE */}
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          {isUnitContext ? "Unit Utilities" : "Property Utilities"}
        </Typography>

        {/* ADD BUTTON */}
        {canCreate && (
          <Button
            variant="contained"
            sx={{ mb: 2 }}
            onClick={handleAdd}
          >
            + Add Utility
          </Button>
        )}

        {/* NO DATA */}
        {utilities.length === 0 ? (
          <Typography>No utilities found.</Typography>
        ) : (
          <Paper elevation={2} sx={{ p: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Supplier Name</strong></TableCell>
                  <TableCell><strong>Email Address</strong></TableCell>
                  <TableCell><strong>Contact Number</strong></TableCell>
                  <TableCell><strong>Meter Number</strong></TableCell>
                  {showActions && <TableCell><strong>Actions</strong></TableCell>}
                </TableRow>
              </TableHead>

              <TableBody>
                {utilities.map((u) => (
                  <TableRow
                    key={u.id}
                    hover={canEdit}
                    onClick={() => {
                      if (canEdit) {
                        navigate(
                          isUnitContext
                            ? `/units/${unitId}/utilities/${u.id}/edit`
                            : `/properties/${propertyId}/utilities/${u.id}/edit`
                        );
                      }
                    }}
                    sx={{ cursor: canEdit ? "pointer" : "default" }}
                  >
                    <TableCell>{u.utility_type || "-"}</TableCell>
                    <TableCell>{u.supplier_name || "-"}</TableCell>
                    <TableCell>{u.email_address || "-"}</TableCell>
                    <TableCell>{u.contact_number || "-"}</TableCell>
                    <TableCell>{u.meter_number || "-"}</TableCell>

                    {showActions && (
                      <TableCell>
                        {canDelete && (
                          <IconButton
                            color="error"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDelete(u.id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      {/* TOAST / SNACKBAR */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default UtilityListPage;
